import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  LayoutDashboard, Wallet, Send, ArrowLeftRight, Menu,
  CreditCard, Users, MessageCircle, Crown, HelpCircle,
  Settings, LogOut, X, ChevronRight, Layers, ArrowRightLeft,
  Building2, Shield, Receipt, User, Inbox, BookOpen, Plug,
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ClientAvatar } from './ClientAvatar';
import { useUnreadChatCount } from '@/hooks/useUnreadChatCount';

const mainItems = [
  { label: 'Home', icon: LayoutDashboard, path: '/app' },
  { label: 'Accounts', icon: Wallet, path: '/app/accounts' },
  { label: 'Pay', icon: Send, path: '/app/payments' },
  { label: 'FX', icon: ArrowLeftRight, path: '/app/exchange' },
];

const moreNav = [
  { label: 'Statements', icon: Receipt, path: '/app/statements' },
  { label: 'Cards', icon: CreditCard, path: '/app/cards' },
  { label: 'Beneficiaries', icon: Users, path: '/app/beneficiaries' },
  { label: 'Messages', icon: MessageCircle, path: '/app/chat' },
];

const supportNav = [
  { label: 'Help & Support', icon: HelpCircle, path: '/app/support' },
  { label: 'Plan & Benefits', icon: Crown, path: '/app/plan' },
  { label: 'Settings', icon: Settings, path: '/app/settings' },
  { label: 'Personal Details', icon: User, path: '/app/settings/personal' },
];

export function CustomerBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const { profile, signOut, availableContexts, switchProfile, user } = useCustomerAuth();
  const isCorporate = profile?.client_type === 'corporate';
  const hasMultipleProfiles = availableContexts.length > 1;
  const unreadChatCount = useUnreadChatCount();

  // Check program access for corporate users
  const { data: hasProgramAccess } = useQuery({
    queryKey: ['program-access-check-mobile', user?.id, profile?.id],
    enabled: !!user?.id && isCorporate,
    queryFn: async () => {
      const { data: tm } = await (supabase as any)
        .from('program_team_members')
        .select('id')
        .eq('user_id', user!.id)
        .eq('status', 'active')
        .limit(1)
        .maybeSingle();
      if (tm) return true;
      if (profile?.id) {
        const { data: pm } = await (supabase as any)
          .from('program_managers')
          .select('id')
          .eq('client_id', profile.id)
          .eq('status', 'active')
          .limit(1)
          .maybeSingle();
        if (pm) return true;
      }
      return false;
    },
  });

  const isActive = (path: string) =>
    location.pathname === path ||
    (path !== '/app' && location.pathname.startsWith(path));

  const handleNav = (path: string) => {
    navigate(path);
    setMenuOpen(false);
  };

  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 bg-card/95 backdrop-blur-xl border-t border-border/60 z-50 safe-area-inset-bottom shadow-lg">
        <div className="flex items-center justify-around h-[68px] max-w-lg mx-auto px-2">
          {mainItems.map((item) => {
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`flex flex-col items-center gap-1 px-3 py-1.5 rounded-xl transition-all duration-200 min-w-[60px] ${
                  active
                    ? 'text-[hsl(var(--accent))]'
                    : 'text-muted-foreground active:scale-95'
                }`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${
                  active ? 'bg-gradient-accent-soft shadow-glow-soft' : ''
                }`}>
                  <item.icon className={`w-5 h-5 ${active ? 'stroke-[2.5]' : ''}`} />
                </div>
                <span className="text-[10px] font-bold font-display tracking-tight">{item.label}</span>
              </button>
            );
          })}
          {/* More button — opens full menu sheet */}
          <button
            onClick={() => setMenuOpen(true)}
            className={`flex flex-col items-center gap-1 px-3 py-1.5 rounded-xl transition-all duration-200 min-w-[60px] ${
              menuOpen ? 'text-[hsl(var(--accent))]' : 'text-muted-foreground active:scale-95'
            }`}
          >
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${
              menuOpen ? 'bg-gradient-accent-soft shadow-glow-soft' : ''
            }`}>
              <Menu className={`w-5 h-5 ${menuOpen ? 'stroke-[2.5]' : ''}`} />
            </div>
            <span className="text-[10px] font-bold font-display tracking-tight">More</span>
          </button>
        </div>
      </nav>

      {/* Full-screen menu sheet */}
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="bottom" className="h-[85vh] rounded-t-3xl px-0 pb-24">
          <SheetHeader className="px-5 pb-3">
            <SheetTitle className="text-left text-lg font-extrabold tracking-tight">Menu</SheetTitle>
          </SheetHeader>

          <div className="overflow-y-auto h-full px-4 space-y-5" style={{ scrollbarWidth: 'none' }}>
            {/* User card */}
            <div className="flex items-center gap-3 px-2 py-2">
              <div className="w-10 h-10">
                {isCorporate ? (
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm border bg-gradient-to-br from-blue-500/30 to-blue-400/10 text-blue-400 border-blue-500/20">
                    <Building2 className="w-5 h-5" />
                  </div>
                ) : (
                  <ClientAvatar size="md" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">
                  {isCorporate ? (profile?.company_name || 'Business') : (profile?.full_name || 'User')}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {isCorporate ? 'Business Account' : 'Personal Account'}
                </p>
              </div>
              {profile?.hashtag && (
                <span className="text-[10px] font-mono font-bold text-accent bg-accent/10 px-2 py-1 rounded-lg border border-accent/25">
                  {profile.hashtag}
                </span>
              )}
            </div>

            {/* Profile Switcher */}
            {hasMultipleProfiles && (
              <div className="space-y-1 px-1">
                {availableContexts
                  .filter(ctx => ctx.client_id !== profile?.id)
                  .map(ctx => (
                    <button
                      key={ctx.client_id}
                      onClick={async () => {
                        await switchProfile(ctx.client_id);
                        handleNav('/app');
                      }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-all"
                    >
                      <ArrowRightLeft className="w-4 h-4" />
                      <span className="truncate">Switch to {ctx.label}</span>
                      {ctx.client_type === 'corporate' && (
                        <span className="text-[8px] font-bold uppercase px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 ml-auto shrink-0">BIZ</span>
                      )}
                    </button>
                  ))}
              </div>
            )}

            {/* Banking nav */}
            <div>
              <p className="px-2 pb-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Banking</p>
              <div className="space-y-0.5">
                {moreNav.map(item => {
                  const active = isActive(item.path);
                  return (
                    <button
                      key={item.path}
                      onClick={() => handleNav(item.path)}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-all ${
                        active
                          ? 'bg-accent/15 text-accent'
                          : 'text-foreground hover:bg-secondary/60'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        active ? 'bg-accent/20' : 'bg-secondary/60'
                      }`}>
                        <item.icon className="w-[18px] h-[18px]" />
                      </div>
                      <span className="flex-1 text-left">{item.label}</span>
                      {item.path === '/app/chat' && unreadChatCount > 0 && !active && (
                        <span className="w-5 h-5 rounded-full bg-accent text-accent-foreground text-[10px] font-bold flex items-center justify-center shrink-0">
                          {unreadChatCount > 9 ? '9+' : unreadChatCount}
                        </span>
                      )}
                      {active && <ChevronRight className="w-4 h-4 opacity-50" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Corporate section */}
            {isCorporate && (
              <div>
                <p className="px-2 pb-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Corporate</p>
                {[
                  { label: 'Team Management', icon: Users, path: '/app/team' },
                  { label: 'Bookkeeping', icon: BookOpen, path: '/app/bookkeeping' },
                  { label: 'Integrations', icon: Plug, path: '/app/integrations' },
                ].map(item => (
                  <button
                    key={item.path}
                    onClick={() => handleNav(item.path)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-all ${
                      isActive(item.path)
                        ? 'bg-blue-500/15 text-blue-400'
                        : 'text-foreground hover:bg-secondary/60'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      isActive(item.path) ? 'bg-blue-500/20' : 'bg-secondary/60'
                    }`}>
                      <item.icon className="w-[18px] h-[18px]" />
                    </div>
                    <span className="flex-1 text-left">{item.label}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Card Program */}
            {hasProgramAccess && (
              <div>
                <p className="px-2 pb-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Card Program</p>
                <button
                  onClick={() => handleNav('/app/program')}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-all ${
                    isActive('/app/program')
                      ? 'bg-emerald-500/15 text-emerald-400'
                      : 'text-foreground hover:bg-secondary/60'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    isActive('/app/program') ? 'bg-emerald-500/20' : 'bg-secondary/60'
                  }`}>
                    <Layers className="w-[18px] h-[18px]" />
                  </div>
                  <span className="flex-1 text-left">Card Program</span>
                </button>
              </div>
            )}

            {/* Support & Settings */}
            <div>
              <p className="px-2 pb-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Support & Settings</p>
              <div className="space-y-0.5">
                {supportNav.map(item => {
                  const active = isActive(item.path);
                  return (
                    <button
                      key={item.path}
                      onClick={() => handleNav(item.path)}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-all ${
                        active
                          ? 'bg-accent/15 text-accent'
                          : 'text-foreground hover:bg-secondary/60'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        active ? 'bg-accent/20' : 'bg-secondary/60'
                      }`}>
                        <item.icon className="w-[18px] h-[18px]" />
                      </div>
                      <span className="flex-1 text-left">{item.label}</span>
                      {active && <ChevronRight className="w-4 h-4 opacity-50" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Sign out */}
            <button
              onClick={async () => {
                await signOut();
                navigate('/app/login');
              }}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-destructive hover:bg-destructive/10 transition-all"
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-destructive/10">
                <LogOut className="w-[18px] h-[18px]" />
              </div>
              <span className="flex-1 text-left">Sign Out</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
